import { createFileRoute, notFound } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { isUuid } from '@leanin/shared';
import { useMember, useAuthedSession } from '@/lib/use-session';
import { eventsKeys } from '@/lib/queries/events';
import { groupKeys } from '@/lib/queries/groups';
import { adaptEventDetail } from '@/components/events/event-adapters';
import type { EventDetail as EventDetailDomain } from '@leanin/shared/views/event';
import { EventDetailView } from './-components/EventDetailView';
import { isNotFoundApiError } from '@/lib/api-error';
import { NotFoundPage } from '@/components/shell/NotFoundPage';

export const Route = createFileRoute('/_authed/events/$id/')({
  beforeLoad: ({ params }) => {
    if (!isUuid(params.id)) throw notFound();
  },
  component: EventDetailPage,
});

function EventDetailPage() {
  const { id } = Route.useParams();
  const member = useMember();
  const session = useAuthedSession();

  const detailQuery = useQuery(eventsKeys.detail(id));
  const attendeesQuery = useQuery({
    ...eventsKeys.attendees(id, 6),
    enabled: detailQuery.data != null,
  });

  const detail = detailQuery.data;
  const isOrganizer =
    detail != null &&
    session != null &&
    detail.organizerUserId !== null &&
    detail.organizerUserId === session.userId;
  const isPlatformAdmin = session?.platformRole === 'admin';
  const needsGroupRole = detail != null && !isOrganizer && !isPlatformAdmin;

  const groupRoleQuery = useQuery({
    ...groupKeys.viewerRole(detail?.groupId ?? ''),
    enabled: needsGroupRole,
  });

  // 404 → branded NotFoundPage directly. (The previous duck-type check
  // for `error.status === 404` never fired because the openapi-fetch
  // error path threw a generic DomainError without a `status` field.)
  if (isNotFoundApiError(detailQuery.error)) return <NotFoundPage />;

  // Hold the page entirely while the session loads. The header builder
  // (skeleton or live) can't paint a useful chrome without a viewer context,
  // and gating here keeps the EventDetailView mount-once contract simple.
  if (member === null) return null;

  const event =
    detail != null
      ? adaptEventDetail(detail as unknown as EventDetailDomain)
      : null;

  const isLeader = groupRoleQuery.data === 'leader';
  const canManage = isOrganizer || isPlatformAdmin || isLeader;

  return (
    <EventDetailView
      eventId={id}
      event={event}
      attendees={attendeesQuery.data ?? null}
      canManage={canManage}
      isOrganizer={isOrganizer}
    />
  );
}
